import { z } from "zod";
import type { Operation } from "../types";
import { sourceIntentCompileResponse, sourceIntentReviewResponse, verificationEvidence } from "./verification-evidence";

export const agentIntentObserveBody = z.object({
  sessionId: z.number().int().positive(), permissionActionSeq: z.number().int().positive(),
  promptActionSeq: z.number().int().positive(), observationActionSeq: z.number().int().positive(),
}).strict();
export type AgentIntentObserveBody = z.infer<typeof agentIntentObserveBody>;
export const agentIntentObserveResponse = z.object({
  intakeId: z.string().min(1), workerActorId: z.string().min(1), sessionId: z.number().int().positive(),
  promptActionSeq: z.number().int().positive(), observationActionSeq: z.number().int().positive(),
  replayed: z.boolean(), compilation: sourceIntentCompileResponse, review: sourceIntentReviewResponse,
  contractAccepted: z.literal(false), executionProven: z.literal(false),
}).strict();
const observe: Operation<void, void, AgentIntentObserveBody, z.infer<typeof agentIntentObserveResponse>> = {
  method: "POST", path: () => "/api/verification/agent-intent/observe",
  body: agentIntentObserveBody, response: agentIntentObserveResponse,
  invalidates: [verificationEvidence.sourceIntentGet, verificationEvidence.sourceIntentSources,
    verificationEvidence.sourceIntentLibrary, verificationEvidence.sourceIntentCompileStatus],
};
export const verificationAgentIntent = { observe };

/** Adapt the actual session.action notification shape. This is only a dispatch
 * hint: the endpoint reloads and authenticates every referenced durable row. */
export function agentIntentCommandFromSessionNotification(urn: string, params: unknown): AgentIntentObserveBody | null {
  const sessionMatch = /^live_session:([1-9][0-9]*)$/.exec(urn);
  const event = z.object({ seq: z.number().int().positive(), actorId: z.string().min(1),
    action: z.object({ kind: z.literal("checkpoint"), payload: z.object({
      stage: z.literal("task-intent-observed"), permissionActionSeq: z.number().int().positive(),
      promptActionSeq: z.number().int().positive(), promptPayloadHash: z.string().regex(/^[a-f0-9]{64}$/),
    }) }),
  }).safeParse(params);
  if (!sessionMatch || !event.success) return null;
  const command = agentIntentObserveBody.safeParse({ sessionId: Number(sessionMatch[1]),
    permissionActionSeq: event.data.action.payload.permissionActionSeq,
    promptActionSeq: event.data.action.payload.promptActionSeq, observationActionSeq: event.data.seq });
  return command.success ? command.data : null;
}

/** One observer instance per account/session subscription. Mark before dispatch
 * so replayed notifications or ambiguous network failures cannot spin inference.
 * Errors remain observable; an explicit retry can reuse the same endpoint body.
 * Recreate the observer when the current account/session subscription changes. */
export function createAgentIntentCheckpointObserver(input: {
  urn: string;
  dispatch: (body: AgentIntentObserveBody) => Promise<unknown>;
  onError: (error: unknown, body: AgentIntentObserveBody) => void;
}) {
  const seen = new Set<string>();
  return async (params: unknown): Promise<boolean> => {
    const body = agentIntentCommandFromSessionNotification(input.urn, params);
    if (!body) return false;
    const key = `${body.sessionId}:${body.promptActionSeq}`;
    if (seen.has(key)) return false;
    seen.add(key);
    try { await input.dispatch(body); }
    catch (error) { input.onError(error, body); }
    return true;
  };
}
